import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { mediaManagerClient } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';
import { PaginatedResponse } from '@/types/api';

import { TClip } from '../types';

import { CLIPS_PAGE_SIZE, getNextClipsOffset } from './infinite-clips-page';
import { clipsQueryKeys } from './query-keys';

type TGetClips = {
  filters?: Partial<ProgramBroadcastFiltersState>;
  id: string;
};
export async function getBroadcastClips({
  filters,
  id,
}: TGetClips): Promise<PaginatedResponse<TClip, undefined>> {
  return mediaManagerClient.get(`/api/v1/program_broadcasts/${id}/clips`, {
    params: {
      limit: filters?.rowsPerPage,
      offset: filters?.offset,
      ...(filters?.search && { search: filters.search }),
      ...(filters?.gte && { start_datetime__gte: filters.gte }),
      ...(filters?.lte && { end_datetime__lte: filters.lte }),
      ...(filters?.channelType && { channel_type__in: filters.channelType }),
      ...(filters?.review_states &&
        filters.review_states.length > 0 && {
          review_state__in: filters.review_states.join(','),
        }),
      ...(filters?.time_keeping_states &&
        filters.time_keeping_states.length > 0 && {
          time_keeping_states: filters.time_keeping_states.join(','),
        }),
      ...(filters?.logos && filters?.logos.length > 0 && { logo_id__in: filters.logos.join(',') }),
      ...(filters?.broadcasters &&
        filters?.broadcasters.length > 0 && {
          channel_id__in: filters.broadcasters.join(','),
        }),
      ...(filters?.programs &&
        filters?.programs.length > 0 && {
          program_id__in: filters.programs.join(','),
        }),
      ...(filters?.people &&
        filters?.people.length > 0 && {
          people_id__in: filters.people.join(','),
        }),
      ...(filters?.workspaces &&
        filters?.workspaces.length > 0 && {
          workspace_id__in: filters.workspaces.join(','),
        }),
      ...(filters?.projects &&
        filters?.projects.length > 0 && {
          project_id__in: filters.projects.join(','),
        }),
      ...(filters?.clients &&
        filters?.clients.length > 0 && {
          client_id__in: filters.clients.join(','),
        }),
      ...(filters?.tags &&
        filters?.tags.length > 0 && {
          tag_id__in: filters.tags.join(','),
        }),
      ...(filters?.programs &&
        filters?.programs.length > 0 && {
          program_id__in: filters.programs.join(','),
        }),
      ...(filters?.order_by && { order_by: filters.order_by }),
    },
  });
}

type QueryFnType = typeof getBroadcastClips;

interface UseBroadcastClipParams {
  config?: QueryConfig<QueryFnType>;
  filters?: Partial<ProgramBroadcastFiltersState>;
  id?: string;
}

export function useGetBroadcastClips({ config, filters, id = '' }: UseBroadcastClipParams = {}) {
  return useQuery({
    enabled: !!id,
    ...config,
    queryKey: clipsQueryKeys.byId(id, filters),
    queryFn: () => getBroadcastClips({ filters, id }),
  });
}

export function useInfiniteBroadcastClips({ filters, id = '' }: UseBroadcastClipParams = {}) {
  return useInfiniteQuery({
    enabled: !!id,
    // A search, a filter or a sort change is a new key with no data. Without this the panel — the
    // toolbar and its search box included — is replaced by a skeleton, which loses the caret the
    // reviewer is typing into. Keeping the previous batch holds the panel mounted until the first
    // batch of the new result arrives.
    // Only while there is a programme to list, so an empty id cannot leave someone else's cuts
    // on screen. See `useInfiniteSegmentClips`, where that state is reachable.
    placeholderData: id ? keepPreviousData : undefined,
    queryKey: clipsQueryKeys.infiniteByProgram(id, filters),
    queryFn: ({ pageParam }) =>
      getBroadcastClips({
        id,
        filters: { ...filters, offset: pageParam, rowsPerPage: CLIPS_PAGE_SIZE },
      }),
    initialPageParam: 0,
    getNextPageParam: getNextClipsOffset,
  });
}
